import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Heart, WarningCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useEffect } from 'react';
import api from '../api';

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    nombre: '',
    apellido: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'alumno'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [options, setOptions] = useState({ instituciones: [], preselected: null });
  const [institutionId, setInstitutionId] = useState('');
  const [establishmentId, setEstablishmentId] = useState('');
  const [pendingMsg, setPendingMsg] = useState('');

  useEffect(() => {
    api.get(`/public/registration-options?host=${window.location.hostname}`)
      .then(r => {
        setOptions(r.data);
        if (r.data.preselected) {
          setInstitutionId(r.data.preselected.institution_id || '');
          setEstablishmentId(r.data.preselected.establishment_id);
        }
      }).catch(() => {});
  }, []);

  const selectedInst = options.instituciones.find(i => i.id === institutionId);
  const selectedEst = selectedInst?.establecimientos?.find(e => e.id === establishmentId);
  const allowInstructor = selectedEst?.allow_instructor_requests;

  if (pendingMsg) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="max-w-md w-full" data-testid="pending-approval-card">
          <CardHeader>
            <CardTitle className="text-lg">Solicitud enviada</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-600">{pendingMsg}</p>
            <Button className="w-full" onClick={() => navigate('/login')}>Volver al inicio</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    if (formData.password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    if (options.instituciones.length > 0 && !establishmentId) {
      setError('Debes seleccionar tu establecimiento');
      return;
    }

    setLoading(true);

    try {
      const resp = await api.post('/auth/register', {
        nombre: formData.nombre,
        apellido: formData.apellido,
        email: formData.email,
        password: formData.password,
        role: allowInstructor ? formData.role : 'alumno',
        establishment_id: establishmentId || null,
        institution_id: institutionId || null
      });
      if (resp.data.pending_approval) {
        setPendingMsg(resp.data.message);
        toast.success('Solicitud enviada');
        return;
      }
      toast.success('Registro exitoso. Inicia sesión con tus credenciales.');
      navigate('/login');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al registrarse');
      toast.error('Error al registrarse');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <img src="/images/simuclinic-logo.png" alt="SimuClinic" className="h-28 mx-auto" style={{ objectFit: 'contain' }} />
          </Link>
          <p className="text-slate-500 mt-4">Sistema de Simulación Clínica</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Crear Cuenta</CardTitle>
            <CardDescription>
              Completa tus datos para registrarte en el sistema
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-red-50 text-red-600 p-3 rounded-md flex items-center gap-2 text-sm">
                  <WarningCircle size={18} />
                  {error}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="nombre">Nombre</Label>
                  <Input
                    id="nombre"
                    name="nombre"
                    placeholder="Juan"
                    value={formData.nombre}
                    onChange={handleChange}
                    required
                    data-testid="register-nombre-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="apellido">Apellido</Label>
                  <Input
                    id="apellido"
                    name="apellido"
                    placeholder="Pérez"
                    value={formData.apellido}
                    onChange={handleChange}
                    required
                    data-testid="register-apellido-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Correo electrónico</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="tu@email.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="register-email-input"
                />
              </div>

              {options.instituciones.length > 0 && !options.preselected?.locked && (
                <>
                  <div className="space-y-2">
                    <Label>Institución</Label>
                    <Select value={institutionId}
                      onValueChange={(v) => { setInstitutionId(v); setEstablishmentId(''); }}>
                      <SelectTrigger data-testid="register-institution-select">
                        <SelectValue placeholder="Selecciona tu institución" />
                      </SelectTrigger>
                      <SelectContent>
                        {options.instituciones.map(i => <SelectItem key={i.id} value={i.id}>{i.nombre}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Establecimiento</Label>
                    <Select value={establishmentId}
                      onValueChange={setEstablishmentId}>
                      <SelectTrigger data-testid="register-establishment-select">
                        <SelectValue placeholder="Selecciona tu establecimiento" />
                      </SelectTrigger>
                      <SelectContent>
                        {(selectedInst?.establecimientos || []).map(e => (
                          <SelectItem key={e.id} value={e.id}>{e.nombre}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              {options.preselected?.locked && (
                <div className="rounded-lg border border-blue-100 bg-blue-50 p-3 text-sm text-blue-900" data-testid="register-locked-establishment">
                  Te estás registrando en <strong>{options.preselected.establishment_nombre}</strong>.
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="role">Tipo de usuario</Label>
                <Select
                  value={allowInstructor ? formData.role : 'alumno'}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, role: value }))}
                >
                  <SelectTrigger data-testid="register-role-select">
                    <SelectValue placeholder="Selecciona tu rol" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="alumno">Alumno</SelectItem>
                    {allowInstructor && <SelectItem value="instructor">Instructor</SelectItem>}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Contraseña</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  data-testid="register-password-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirmar contraseña</Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  data-testid="register-confirm-password-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={loading}
                data-testid="register-submit-btn"
              >
                {loading ? 'Registrando...' : 'Crear cuenta'}
              </Button>

              <p className="text-center text-sm text-slate-500">
                ¿Ya tienes cuenta?{' '}
                <Link
                  to="/login"
                  className="text-blue-600 hover:underline"
                  data-testid="login-link"
                >
                  Inicia sesión
                </Link>
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
